import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteShell from '@/components/feature/SiteShell';
import MovieCard from '@/components/base/MovieCard';
import { usePageMeta } from '@/hooks/usePageMeta';
import { hotMovies, searchMovies } from '@/lib/catalog';

const TYPE_TABS = [
  { key: 'all', label: '全部' },
  { key: 'movie', label: '电影' },
  { key: 'tv', label: '电视剧' },
  { key: 'variety', label: '综艺' },
] as const;

const SORTS = [
  { key: 'relevance', label: '相关度' },
  { key: 'rating', label: '评分' },
  { key: 'updated', label: '更新时间' },
] as const;

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [input, setInput] = useState(query);
  const [activeType, setActiveType] = useState<'all' | 'movie' | 'tv' | 'variety'>('all');
  const [sort, setSort] = useState<'relevance' | 'rating' | 'updated'>('relevance');

  usePageMeta({
    title: query ? `“${query}”的搜索结果 - 黄牛视频` : '搜索影视作品 - 黄牛视频',
    description: query
      ? `在黄牛视频搜索“${query}”，查看匹配的电影、电视剧与综艺作品，并可按类型筛选结果。`
      : '黄牛视频搜索页，支持按影视作品名称、导演、演员与类型进行模糊搜索。',
    keywords: '影视搜索,影片搜索,黄牛视频,电影搜索,电视剧搜索',
  });

  useEffect(() => {
    setInput(query);
    setActiveType('all');
    setSort('relevance');
  }, [query]);

  const matched = useMemo(() => searchMovies(query), [query]);

  const results = useMemo(() => {
    const byType =
      activeType === 'all' ? matched : matched.filter((movie) => movie.category === activeType);
    if (sort === 'rating') return [...byType].sort((a, b) => b.rating - a.rating);
    if (sort === 'updated') return [...byType].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    return byType;
  }, [matched, activeType, sort]);

  const submit = (value: string) => {
    const next = value.trim();
    setSearchParams(next ? { q: next } : {});
  };

  const suggestions = ['长夜将明', '长安旧事', '声浪撞地球', '悬疑', '逆风少年', '修复'];

  return (
    <SiteShell>
      <section className="w-full border-b border-background-200 bg-background-100/60 px-4 py-8 md:px-8 md:py-10">
        <div className="max-w-3xl">
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-3xl">
            搜索影视作品
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            输入作品名称、导演、演员或类型关键词，支持模糊匹配。
          </p>

          <form
            className="mt-5 flex flex-col gap-2 sm:flex-row"
            onSubmit={(event) => {
              event.preventDefault();
              submit(input);
            }}
          >
            <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5 focus-within:border-primary-300">
              <i className="ri-search-line text-base text-foreground-400" />
              <input
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="例如：长夜将明 / 悬疑 / 沈知远"
                aria-label="搜索影视作品"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              {input && (
                <button
                  type="button"
                  aria-label="清空"
                  onClick={() => setInput('')}
                  className="cursor-pointer text-foreground-400 hover:text-foreground-700"
                >
                  <i className="ri-close-circle-fill text-sm" />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-900 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-800"
            >
              搜索
            </button>
          </form>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-foreground-400">热门搜索</span>
            {suggestions.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => submit(tag)}
                className="cursor-pointer rounded-full bg-background-50 px-2.5 py-1 text-[11px] text-foreground-600 transition-colors hover:bg-secondary-100 hover:text-secondary-900"
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 pb-14 pt-6 md:px-8 md:pb-16 md:pt-8">
        {query ? (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-foreground-600">
                关于 <span className="font-semibold text-foreground-950">“{query}”</span> 的结果：
                共 <span className="font-label font-bold text-accent-700">{results.length}</span> 部
                {activeType !== 'all' && <span className="text-foreground-400">（已按类型筛选）</span>}
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-1 rounded-full border border-background-200 bg-background-50 p-1">
                  {TYPE_TABS.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => setActiveType(tab.key)}
                      className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors ${
                        activeType === tab.key
                          ? 'bg-primary-900 font-medium text-background-50'
                          : 'text-foreground-600 hover:bg-background-100'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-1 text-xs text-foreground-500">
                  <i className="ri-sort-desc text-sm" />
                  {SORTS.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => setSort(option.key)}
                      className={`cursor-pointer whitespace-nowrap rounded px-1.5 py-1 transition-colors ${
                        sort === option.key
                          ? 'font-semibold text-primary-700'
                          : 'text-foreground-500 hover:text-foreground-800'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {results.length > 0 ? (
              <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4 xl:grid-cols-6">
                {results.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <div className="mt-6">
                <div className="rounded-lg border border-dashed border-background-300 bg-background-100/50 px-4 py-12 text-center">
                  <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-2xl text-foreground-400">
                    <i className="ri-search-eye-line" />
                  </span>
                  <h2 className="mt-4 font-heading text-base font-bold text-foreground-900">
                    没有找到与“{query}”匹配的作品
                  </h2>
                  <p className="mx-auto mt-2 max-w-md text-xs leading-relaxed text-foreground-500">
                    试试更换关键词，或减少类型筛选。也可以直接浏览下面的热门作品与分类频道。
                  </p>
                  <div className="mt-5 flex flex-wrap justify-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setActiveType('all');
                        setSort('relevance');
                      }}
                      className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-xs text-foreground-700 hover:bg-background-100"
                    >
                      重置筛选
                    </button>
                    <Link
                      to="/category/movie"
                      className="cursor-pointer whitespace-nowrap rounded-md bg-primary-900 px-4 py-2 text-xs font-medium text-background-50 hover:bg-primary-800"
                    >
                      浏览电影频道
                    </Link>
                  </div>
                </div>

                <div className="mt-8">
                  <h2 className="mb-4 font-heading text-base font-bold text-foreground-950">
                    热门推荐
                  </h2>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-6">
                    {hotMovies(6).map((movie) => (
                      <MovieCard key={movie.id} movie={movie} />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950">
                还没有输入关键词
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                在上方输入影视作品名称、导演或类型即可开始搜索。当前搜索为前端示例逻辑，数据来源于本地示例内容。
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link
                  to="/category/movie"
                  className="rounded-md bg-primary-900 px-4 py-2 text-xs font-medium text-background-50 hover:bg-primary-800"
                >
                  电影频道
                </Link>
                <Link
                  to="/category/tv"
                  className="rounded-md bg-secondary-100 px-4 py-2 text-xs text-secondary-900 hover:bg-secondary-200"
                >
                  电视剧频道
                </Link>
                <Link
                  to="/category/variety"
                  className="rounded-md bg-accent-100 px-4 py-2 text-xs text-accent-900 hover:bg-accent-200"
                >
                  综艺频道
                </Link>
              </div>
            </div>

            <div className="mt-8">
              <h2 className="mb-4 font-heading text-base font-bold text-foreground-950">
                热门作品推荐
              </h2>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-6">
                {hotMovies(6).map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>
          </div>
        )}
      </section>
    </SiteShell>
  );
}